/**
 * PA appointment list hook — adapted from PM's use-appointments.ts.
 * Fetches the day's appointments for the authenticated practitioner.
 *
 * POST /api/Appointment — filter: Day, today's date range, providers:[practitionerId].
 */
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
// PROD-1817: response/request SHAPES now come from `scheduling-api`, which the
// ported PM scheduling libs also use. PA's `appointment-api` has no PM twin, and
// keeping both left two incompatible `AppointmentsDetailsResponse` types meeting
// in calendar-page. Only the type SOURCE moved — the provider-scoping logic below
// (practitionerIds precedence) is untouched and still pinned by
// `provider-scoping.guard.test.ts`.
import type {
  AppointmentsDetailsResponse,
  AppointmentsRequest,
  DateFilter,
} from '@/types/scheduling-api';
import { practiceNow } from '@/shared/lib/practice-time';

/** Pre-computed DATETIME range + filter (from `rangeFor`) for Week/Month views. */
export interface AppointmentsRange {
  startDate: string;
  endDate: string;
  filter: Extract<DateFilter, 'Day' | 'Week' | 'Month'>;
}

export interface UseAppointmentsParams {
  organizationId: string;
  practiceId: string;
  practitionerId?: string;
  /**
   * Multiple provider ids for the `practitioners[]` filter (nurse → assigned
   * providers, Providers view). Takes precedence over `practitionerId` when
   * present. An empty array means "no provider filter" (all in-scope).
   */
  practitionerIds?: string[];
  /** IANA tz from the user profile (e.g. Asia/Hebron); falls back to the browser. */
  timezone?: string;
  searchText?: string;
  /**
   * Optional YYYY-MM-DD to fetch a specific day (Calendar Day view). When
   * omitted (and no `range`) the range defaults to today — preserving the
   * Today page's behavior. Ignored when `range` is provided.
   */
  date?: string;
  /**
   * Optional pre-computed DATETIME range + filter (Calendar Week/Month views,
   * via `rangeFor`). Takes precedence over `date` when present.
   */
  range?: AppointmentsRange;
  /**
   * Server-side status / location filters (PROD-3307 C6). Only the Calendar's MONTH request sends them, as the
   * Practice calendar does (PM `scheduling-page.tsx` `monthAppointmentsParams`): the Month response caps each day at 3,
   * so a client-side filter over those rows would be lossy and the "+N more" count wrong. Absent = no filter.
   */
  status?: AppointmentsRequest['status'];
  location?: string[];
  /**
   * `true` (the default, the Today page and the dashboard) asks the server for active visits only, which drops
   * Cancelled and NoShow. The Calendar sends `false` as the Practice calendar does (PM `use-appointments.ts`), so a
   * cancelled or no-show visit stays on its day and the Cancelled / No-show filters and counts have rows (PROD-3313).
   * Part of `params`, so it is part of the query key: the Calendar's cache never serves the Today page's.
   */
  showActiveAppointmentOnly?: boolean;
}

/**
 * Full-day DATETIME range for `date` (YYYY-MM-DD), or today when omitted.
 * Date-only strings produce a zero-width range on the backend and return 0
 * results, so we widen to T00:00:00.000 / T23:59:59.000 (PM pattern).
 */
function dayRange(date?: string): { startDate: string; endDate: string } {
  let day = date;
  if (!day) {
    const now = practiceNow();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    day = `${yyyy}-${mm}-${dd}`;
  }
  return {
    startDate: `${day}T00:00:00.000`,
    endDate: `${day}T23:59:59.000`,
  };
}

export function useAppointments(params: UseAppointmentsParams | null) {
  return useQuery({
    // Key under `['appointments', params]` so the shared mutation hooks
    // (cancel / reschedule / add-note / status / block) — which optimistically
    // update + invalidate the `['appointments']` PREFIX — actually refresh this
    // calendar, and so the realtime hook's `['appointments', params]` cache key
    // matches. A prior `'pa-appointments-today'` key silently broke both
    // (mutations succeeded server-side but the grid stayed stale). `params`
    // fully discriminates the cache (org/practice/range/date/search). PROD-1193.
    queryKey: ['appointments', params],
    queryFn: ({ signal }) => {
      if (!params) throw new Error('useAppointments called without params');
      // A pre-computed `range` (Week/Month) wins; otherwise widen `date`/today
      // to a single Day range — preserving the Today page's behavior.
      const { startDate, endDate, filter } = params.range
        ? params.range
        : { ...dayRange(params.date), filter: 'Day' as const };
      const body: AppointmentsRequest = {
        searchText: params.searchText ?? '',
        LoggedInUserTimezone:
          params.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        filter,
        startDate,
        endDate,
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        // Provider filter. `practitionerIds` (nurse → assigned providers) wins;
        // otherwise self-filter by the single practitioner id (= user.id /
        // doctorSetupId) — matches the Flutter request, returns the doctor's day.
        practitioners: params.practitionerIds
          ? params.practitionerIds
          : params.practitionerId
            ? [params.practitionerId]
            : [],
        status: params.status ?? [],
        location: params.location ?? [],
        types: [],
        pageNumber: -1,
        pageSize: 50,
        isTimeSelected: false,
        showActiveAppointmentOnly: params.showActiveAppointmentOnly ?? true,
      };
      return post<AppointmentsDetailsResponse>('/api/Appointment', body, { signal });
    },
    enabled: !!params?.practiceId,
    refetchOnMount: 'always',
  });
}
